import React, { useEffect, useMemo, useState } from "react";
import {
  FIELD_ALLERGIES,
  FIELD_HEALTH_GOAL,
  FIELD_MEDS,
  GATE_COMORBID,
  GATE_GLP1,
} from "../../../organisms/PatientDetailsV2/SharedProfile/showIf";
import FieldEditHistory from "../../../organisms/PatientDetailsV2/SharedProfile/FieldEditHistory";
import type { SharedLogMap } from "../../../organisms/PatientDetailsV2/SharedProfile/useSharedProfile";
import { toDisplayString } from "../../../organisms/PatientDetailsV2/CoachAssessment/QuestionField";
import ButtonLoader from "../../../atoms/ButtonLoader/ButtonLoader";
import { ConditionCardIcon, EditPencilIcon } from "./ConditionCardIcons";

type Option = { id: string; label: string };

type PatientSnapshot = {
  name?: string;
  age?: number | null;
  sex?: string;
  phone?: string | null;
  email?: string | null;
};

type Draft = {
  glp1: string;
  comorbidities: string[];
  healthGoal: string;
  medications: string;
  allergies: string;
};

type Props = {
  editing: boolean;
  options: Option[];
  glp1?: unknown;
  comorbidities?: string[];
  healthGoal?: unknown;
  medications?: unknown;
  allergies?: unknown;
  sharedLog?: SharedLogMap;
  patient?: PatientSnapshot;
  saving?: boolean;
  onEdit: () => void;
  onCancel: () => void;
  onSave: (draft: Draft) => void;
};

const asText = (value: unknown) => toDisplayString(value);

const SHARED_PROFILE_GATE_NOTE =
  "Co-morbidities and GLP-1 status decide which Shared Profile sections appear. Name, phone and email stay on the patient record.";

const PatientConditionCard: React.FC<Props> = ({
  editing,
  options,
  glp1,
  comorbidities,
  healthGoal,
  medications,
  allergies,
  sharedLog,
  patient = {},
  saving,
  onEdit,
  onCancel,
  onSave,
}) => {
  const initial = useMemo<Draft>(
    () => ({
      glp1: asText(glp1) || "",
      comorbidities: Array.isArray(comorbidities) ? comorbidities : [],
      healthGoal: asText(healthGoal),
      medications: asText(medications),
      allergies: asText(allergies),
    }),
    [allergies, comorbidities, glp1, healthGoal, medications],
  );
  const [draft, setDraft] = useState<Draft>(initial);

  useEffect(() => {
    if (editing) setDraft(initial);
  }, [editing, initial]);

  const comorbidLabel = (comorbidities || [])
    .map((id) => options.find((option) => option.id === id)?.label || id)
    .join(", ");
  const glp1Text = asText(glp1).toLowerCase();
  const glp1Label =
    glp1Text === "yes" ? "Yes" : glp1Text === "no" ? "No" : asText(glp1);

  const formatComorbidLog = (raw: string) => {
    if (raw === "—" || !raw) return raw;
    return raw
      .split(",")
      .map((part) => part.trim())
      .filter(Boolean)
      .map((id) => options.find((o) => o.id === id)?.label || id)
      .join(", ");
  };

  const formatGlp1Log = (raw: string) => {
    const value = raw.toLowerCase();
    if (value === "yes") return "Yes";
    if (value === "no") return "No";
    return raw;
  };

  const toggleComorbid = (id: string) => {
    setDraft((prev) => {
      const has = prev.comorbidities.includes(id);
      return {
        ...prev,
        comorbidities: has
          ? prev.comorbidities.filter((item) => item !== id)
          : [...prev.comorbidities, id],
      };
    });
  };

  return (
    <div className="modal-card modal-card-wide">
      <div className="mc-title">
        <ConditionCardIcon />
        Patient &amp; condition
        {editing ? (
          <span className="mc-edit-actions">
            <button type="button" className="btn-ghost-mini" onClick={onCancel}>
              Cancel
            </button>
            <button
              type="button"
              className="mc-edit-btn solid"
              disabled={saving}
              onClick={() => onSave(draft)}
            >
              {saving && <ButtonLoader />}
              {saving ? "Saving…" : "Save"}
            </button>
          </span>
        ) : (
          <button type="button" className="mc-edit-btn" onClick={onEdit}>
            <EditPencilIcon />
            Edit
          </button>
        )}
      </div>

      {editing ? (
        <div className="mc-form">
          <label className="mfield">
            Name
            <input className="minp" value={patient.name || ""} readOnly />
          </label>
          <label className="mfield">
            Age
            <input
              className="minp"
              value={patient.age != null ? String(patient.age) : ""}
              readOnly
            />
          </label>
          <label className="mfield">
            Phone
            <input className="minp" value={patient.phone || ""} readOnly />
          </label>
          <label className="mfield">
            Email
            <input className="minp" value={patient.email || ""} readOnly />
          </label>
          <label className="mfield">
            Sex
            <input className="minp" value={patient.sex || ""} readOnly />
          </label>
          <label className="mfield">
            On a GLP-1?
            <div className="mchips">
              {["yes", "no"].map((value) => (
                <button
                  key={value}
                  type="button"
                  className={`mchip ${draft.glp1 === value ? "sel" : ""}`}
                  onClick={() => setDraft((prev) => ({ ...prev, glp1: value }))}
                >
                  {value === "yes" ? "Yes" : "No"}
                </button>
              ))}
            </div>
          </label>
          <label className="mfield full">
            Co-morbidities
            <div className="mchips">
              {options.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  className={`mchip ${
                    draft.comorbidities.includes(option.id) ? "sel" : ""
                  }`}
                  onClick={() => toggleComorbid(option.id)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </label>
          <label className="mfield full">
            Health goal
            <textarea
              className="minp"
              rows={2}
              value={draft.healthGoal}
              onChange={(e) =>
                setDraft((prev) => ({ ...prev, healthGoal: e.target.value }))
              }
            />
          </label>
          <label className="mfield full">
            Current medications
            <textarea
              className="minp"
              rows={2}
              value={draft.medications}
              onChange={(e) =>
                setDraft((prev) => ({ ...prev, medications: e.target.value }))
              }
            />
          </label>
          <label className="mfield full">
            Food / drug allergies
            <input
              className="minp"
              value={draft.allergies}
              onChange={(e) =>
                setDraft((prev) => ({ ...prev, allergies: e.target.value }))
              }
            />
          </label>
          <div className="mc-note">{SHARED_PROFILE_GATE_NOTE}</div>
        </div>
      ) : (
        <div className="mc-rows">
          <div className="mc-row">
            <span className="mc-k">Name</span>
            <span className={`mc-v ${patient.name ? "" : "empty"}`}>
              {patient.name || "—"}
            </span>
          </div>
          <div className="mc-row">
            <span className="mc-k">Age</span>
            <span className={`mc-v ${patient.age != null ? "" : "empty"}`}>
              {patient.age != null ? `${patient.age} yrs` : "—"}
            </span>
          </div>
          <div className="mc-row">
            <span className="mc-k">Sex</span>
            <span className={`mc-v ${patient.sex ? "" : "empty"}`}>
              {patient.sex || "—"}
            </span>
          </div>
          <div className="mc-row mc-row-stack">
            <div className="mc-row">
              <span className="mc-k">On GLP-1</span>
              <span className={`mc-v ${glp1Label ? "" : "empty"}`}>
                {glp1Label || "—"}
              </span>
            </div>
            <FieldEditHistory
              label="On a GLP-1?"
              log={sharedLog?.[GATE_GLP1]}
              formatValue={formatGlp1Log}
            />
          </div>
          <div className="mc-row mc-row-stack">
            <div className="mc-row">
              <span className="mc-k">Co-morbidities</span>
              <span className={`mc-v ${comorbidLabel ? "" : "empty"}`}>
                {comorbidLabel || "—"}
              </span>
            </div>
            <FieldEditHistory
              label="Co-morbidities"
              log={sharedLog?.[GATE_COMORBID]}
              formatValue={formatComorbidLog}
            />
          </div>
          <div className="mc-row mc-row-stack">
            <div className="mc-row">
              <span className="mc-k">Health goal</span>
              <span className={`mc-v ${asText(healthGoal) ? "" : "empty"}`}>
                {asText(healthGoal) || "—"}
              </span>
            </div>
            <FieldEditHistory
              label="Health goal"
              log={sharedLog?.[FIELD_HEALTH_GOAL]}
            />
          </div>
          <div className="mc-row mc-row-stack">
            <div className="mc-row">
              <span className="mc-k">Current medications</span>
              <span className={`mc-v ${asText(medications) ? "" : "empty"}`}>
                {asText(medications) || "—"}
              </span>
            </div>
            <FieldEditHistory
              label="Current medications"
              log={sharedLog?.[FIELD_MEDS]}
            />
          </div>
          <div className="mc-row mc-row-stack">
            <div className="mc-row">
              <span className="mc-k">Allergies</span>
              <span className={`mc-v ${asText(allergies) ? "" : "empty"}`}>
                {asText(allergies) || "—"}
              </span>
            </div>
            <FieldEditHistory
              label="Food / drug allergies"
              log={sharedLog?.[FIELD_ALLERGIES]}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export const conditionDraftToAnswers = (draft: Draft) => ({
  [GATE_GLP1]: draft.glp1 || null,
  [GATE_COMORBID]: draft.comorbidities,
  [FIELD_HEALTH_GOAL]: draft.healthGoal,
  [FIELD_MEDS]: draft.medications,
  [FIELD_ALLERGIES]: draft.allergies,
});

export default PatientConditionCard;
